import { FC, PropsWithChildren, useState, useEffect } from 'react';
import { PrimaryButton } from 'ui/button';
import { Popover, PopoverContent, PopoverTrigger } from 'ui/popover';
import { colors } from 'ui/theme/colors';
import { NegativeFillDiamond } from 'ui/icons/status/NegativeFillDiamond';
import {
  getPreviewUsersLeadingText,
  getPreviewUsersCountText,
} from 'modules/predictiveGoals/components/previewUsers/previewUtils';
import {
  PREVIEW_USERS_MET_CRITERIA,
  TEST_IDS,
  PREVIEW_USERS_ERROR_LEAD,
  PREVIEW_USERS_ERROR_BODY,
} from '../../constants';
import {
  Body2Bold,
  ApproxUserCount,
  MutatePreviewUserContentContainer,
  MutatePreviewUsersTextContent,
  UserPreviewTextContainer,
  UserPreviewContentContainer,
} from './PreviewUsers.styles';

export const MUTATE_PREVIEW_ERROR_ID = 'mutate-preview-users-error';
export interface MutatePreviewUsersProps {
  children?: JSX.Element;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClick: () => void;
  isError?: boolean;
  showPreviewContent?: boolean;
  usersCount: number;
  primaryButtonConfig: {
    testId: string;
    text: string;
  };
}

export const MutatePreviewUsers: FC<PropsWithChildren<MutatePreviewUsersProps>> = ({
  isDisabled = false,
  isError = false,
  isLoading = false,
  onClick,
  primaryButtonConfig,
  showPreviewContent = false,
  usersCount,
}) => {
  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const onSetPopover = (open: boolean) => {
    // Displaying the content controlled externally, so return if popover attempting to open
    if (open) {
      return;
    }
    // Allow all other events to close popover
    setPopoverOpen(open);
  };
  const handleCreateClick = () => {
    setPopoverOpen(false);
    onClick();
  };
  const LEADING_TEXT = getPreviewUsersLeadingText(usersCount);
  const FORMATTED_COLORED_USER_TEXT = getPreviewUsersCountText(usersCount);

  useEffect(() => {
    // Ignore same values
    if (showPreviewContent === isPopoverOpen) {
      return;
    }
    setPopoverOpen(showPreviewContent);
  }, [showPreviewContent]);

  let content = null;
  if (isError) {
    content = (
      <UserPreviewContentContainer>
        <UserPreviewTextContainer data-test={MUTATE_PREVIEW_ERROR_ID}>
          <Body2Bold>{PREVIEW_USERS_ERROR_LEAD}</Body2Bold> {PREVIEW_USERS_ERROR_BODY}
        </UserPreviewTextContainer>
      </UserPreviewContentContainer>
    );
  } else {
    content = (
      <MutatePreviewUserContentContainer>
        <NegativeFillDiamond width={48} height={32} color={colors.red100} />
        <MutatePreviewUsersTextContent>
          <Body2Bold data-test={TEST_IDS.MutateGoalErrorContent}>
            Try again with modified criteria.
          </Body2Bold>
          {` ${LEADING_TEXT}`}
          <ApproxUserCount $color={colors.textSecondary}>
            {` ${FORMATTED_COLORED_USER_TEXT} `}
          </ApproxUserCount>
          {PREVIEW_USERS_MET_CRITERIA}
        </MutatePreviewUsersTextContent>
      </MutatePreviewUserContentContainer>
    );
  }

  return (
    <Popover open={isPopoverOpen} onOpenChange={onSetPopover}>
      <PopoverTrigger asChild>
        <PrimaryButton
          data-test={primaryButtonConfig.testId}
          isLoading={isLoading}
          isDisabled={isDisabled}
          onClick={handleCreateClick}
        >
          {primaryButtonConfig.text}
        </PrimaryButton>
      </PopoverTrigger>
      <PopoverContent align="end" side="top">
        {content}
      </PopoverContent>
    </Popover>
  );
};
